import { useState } from 'react'
import { Download, Loader2, Printer } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { equipment as equipmentApi } from '@/utils/api'
import type { Equipment, LabelOptions } from '@/types/equipment'
import { loadLabelOptions, saveLabelOptions } from './equipmentUtils'

// Etiketten-PNG hat 313 dpi (Brother VC-500W). Für die Vorschau in echter Größe auf ~96 CSS-dpi umrechnen.
const PREVIEW_SCALE = 96 / 313

interface Props {
  items: Equipment[]
  open: boolean
  onClose: () => void
}

function Choice<T extends string | number>({ value, current, onClick, children }: {
  value: T; current: T; onClick: (v: T) => void; children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={cn(
        'rounded-md border px-3 py-1.5 text-sm transition-colors',
        value === current ? 'border-primary-500 bg-primary-50 text-primary-700' : 'hover:bg-gray-50',
      )}
    >
      {children}
    </button>
  )
}

export default function LabelDialog({ items, open, onClose }: Props) {
  const [opts, setOpts] = useState<LabelOptions>(loadLabelOptions)
  const [busy, setBusy] = useState(false)
  const [previewSize, setPreviewSize] = useState<{ w: number; h: number } | null>(null)

  const update = (patch: Partial<LabelOptions>) => {
    const next = { ...opts, ...patch }
    setOpts(next)
    saveLabelOptions(next)
    setPreviewSize(null)
  }

  const download = async () => {
    setBusy(true)
    try {
      const res = await equipmentApi.downloadLabels(items.map((i) => i.id), opts)
      const single = items.length === 1
      const name = single
        ? `Etikett_${items[0].unique_code}_${opts.tapeMm}mm_${opts.symbology}.png`
        : `Geraete-Etiketten_${opts.tapeMm}mm.zip`
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url
      a.download = name
      a.click()
      URL.revokeObjectURL(url)
      toast.success(single ? 'Etikett heruntergeladen' : `${items.length} Etiketten heruntergeladen`)
    } catch (err: any) {
      toast.error(err?.response?.status === 400 ? 'Ungültige Auswahl' : 'Etiketten-Erzeugung fehlgeschlagen')
    } finally {
      setBusy(false)
    }
  }

  const preview = items[0]

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Printer className="h-5 w-5" /> Etiketten ({items.length})</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <p className="text-sm font-medium">Bandbreite</p>
            <div className="flex gap-2">
              <Choice value={12} current={opts.tapeMm} onClick={(v) => update({ tapeMm: v })}>12 mm</Choice>
              <Choice value={9} current={opts.tapeMm} onClick={(v) => update({ tapeMm: v })}>9 mm</Choice>
            </div>
          </div>
          <div className="space-y-1.5">
            <p className="text-sm font-medium">Code</p>
            <div className="flex gap-2">
              <Choice value="datamatrix" current={opts.symbology} onClick={(v) => update({ symbology: v })}>Data Matrix (empfohlen)</Choice>
              <Choice value="code128" current={opts.symbology} onClick={(v) => update({ symbology: v })}>Strichcode (Code 128)</Choice>
            </div>
            <p className="text-xs text-muted-foreground">
              {opts.symbology === 'datamatrix'
                ? 'Kompakt, aus jedem Winkel lesbar, verträgt Kratzer und Dreck.'
                : 'Länger, muss quer gescannt werden, empfindlicher auf runden oder zerkratzten Flächen.'}
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={opts.withName} onChange={(e) => update({ withName: e.target.checked })} className="h-4 w-4 rounded border-gray-300" />
            Kurzname mit aufs Etikett
          </label>

          {preview && (
            <div className="space-y-1.5">
              <p className="text-sm font-medium">Vorschau in echter Größe {previewSize && <span className="font-normal text-muted-foreground">(≈ {Math.round((previewSize.w / 313) * 25.4)} × {opts.tapeMm} mm)</span>}</p>
              <div className="flex min-h-[64px] items-center justify-center rounded-lg border bg-[repeating-conic-gradient(#f3f4f6_0%_25%,#fff_0%_50%)] bg-[length:16px_16px] p-4">
                <img
                  key={`${preview.id}-${opts.tapeMm}-${opts.symbology}-${opts.withName}`}
                  src={equipmentApi.labelPreviewUrl(preview.id, opts)}
                  alt={`Etikett ${preview.unique_code}`}
                  onLoad={(e) => setPreviewSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
                  style={previewSize ? { width: previewSize.w * PREVIEW_SCALE, height: previewSize.h * PREVIEW_SCALE } : { height: 40 }}
                  className="shadow-sm ring-1 ring-gray-300 [image-rendering:pixelated]"
                />
              </div>
            </div>
          )}

          <div className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
            <p className="mb-1 font-medium text-foreground">Druck mit dem Brother VC-500W</p>
            <ol className="list-decimal space-y-0.5 pl-4">
              <li>PNG herunterladen ({items.length > 1 ? 'ZIP entpacken' : 'eine Datei'}).</li>
              <li>In der App <em>Color Label Editor</em> ein Etikett in {opts.tapeMm} mm anlegen und das Bild importieren (Bildhöhe = Bandbreite).</li>
              <li>Auf eine glatte, entfettete Fläche kleben, nicht auf Griffgummi. ZINK-Etiketten mit transparenter Schutzfolie abdecken.</li>
            </ol>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Schließen</Button>
          <Button onClick={download} disabled={busy || !items.length}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {items.length > 1 ? `${items.length} Etiketten (ZIP)` : 'Etikett (PNG)'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
